import {
    queryProvider,
    commandProvider,
    serviceQueries,
    serviceCommands,
    events,
    on
} from 'elc-service-bus';
import { ProductSkusRequestedUseCase } from '../internal/use-cases/ProductSkusRequestedUseCase';
import { ProductRepository } from '../internal/repositories/ProductRepository';
import { ProductGridSettings } from '../internal/domain/entities/ProductGridSettings';
import { DISPLAYABLE_STATUSES } from '../internal/constants/InventoryStatuses';

interface IProductsV3 {
    productIds?: string[];
    productTags?: string[];
    viewableInventoryStatuses?: string[];
}

interface IFavorites {
    skuBaseIds: number[];
}

interface IFavoriteUpdatedEventData {
    skuBaseId: number;
    isFavorite: boolean;
    isAlreadyAdded?: boolean;
}

interface IInventoryStatus {
    inventory_status: number | string;
    isUpdatedInventorySource?: boolean;
    highVelocityQuantity?: number;
}

export interface IInventoryStatuses {
    [key: string]: IInventoryStatus;
}

export class ServiceBusController {
    private productRepository: ProductRepository;
    private productGridSettings: ProductGridSettings;

    constructor(productRepository: ProductRepository, productGridSettings: ProductGridSettings) {
        this.productRepository = productRepository;
        this.productGridSettings = productGridSettings;
    }

    // @todo: Deprecate this query in favor of GET_SKUS
    @queryProvider(serviceQueries.GET_PRODUCTS)
    public getProductSkus = async ({
        categoryIds,
        productIds,
        skuIds,
        query
    }: {
        categoryIds?: string[];
        productIds?: string[];
        skuIds?: string[];
        query?: string;
    }) => {
        const productSkusRequestedUseCase = new ProductSkusRequestedUseCase(
            { categoryIds, productIds, skuIds },
            query
        );

        return productSkusRequestedUseCase.execute();
    };

    // @todo: Promote this function once GET_PRODUCTS has been deprecated
    @queryProvider(serviceQueries.GET_PRODUCTS_V2)
    public getProductsV2 = async (productIds: string[]) => {
        return this.productRepository.getProductsByProductIds(productIds);
    };

    // @todo: Deprecate this when we rebuild analytics, so never
    @queryProvider(serviceQueries.GET_PRODUCTS_V3)
    public getProductsV3 = async ({
        productIds = [],
        productTags = [],
        viewableInventoryStatuses = DISPLAYABLE_STATUSES
    }: IProductsV3) => {
        return this.productRepository.getProductsForAnalytics(
            productIds,
            productTags,
            viewableInventoryStatuses
        );
    };

    @queryProvider(serviceQueries.GET_SKUS)
    public getSkus = async (skuIds: string[]) => {
        return this.productRepository.getSkuProducts(skuIds);
    };

    @queryProvider(serviceQueries.GET_REVIEW_PRODUCT)
    public getReviewProduct = async (productId: string) => {
        return this.productRepository.getProductsByProductIds([productId]);
    };

    @queryProvider(serviceQueries.GET_SORTBY_VALUE)
    public getSortbyValue = async () => {
        return this.productGridSettings.sortValue;
    };

    @commandProvider(serviceCommands.UPDATE_INVENTORY)
    public updateInventory = (inventory: IInventoryStatuses) => {
        this.productRepository.updateInventoryStatus(inventory);
    };

    @on(events.NAVIGATE)
    public productRouting = async (payload: {
        currentRoute: string;
        params: string[];
        queryParams: {
            size?: string;
            shade?: string;
        };
    }) => {
        const { queryParams } = payload;
        this.productRepository.setRouterQueryParams(queryParams);
    };

    @on(events.FAVORITES_LOADED)
    public initFavorites = ({ skuBaseIds }: IFavorites) => {
        this.productRepository.updateFavorites(skuBaseIds);
    };

    @on(events.FAVORITE_STATUS_UPDATED)
    public updateFavorite = ({
        skuBaseId,
        isFavorite,
        isAlreadyAdded
    }: IFavoriteUpdatedEventData) => {
        if (!isAlreadyAdded) {
            this.productRepository.updateFavorites([skuBaseId], isFavorite);
        }
    };
}
